import React from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Portal,
  Flex,
  Icon,
  Spinner,
  Tooltip,
  PopoverProps,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { colors } from '@/theme/foundations/colors';

// The dark bubble of the light design, and in dark mode the raised grey with
// light text every other tooltip uses (theme/components/ToolTip.js). The
// secondary-950 token alone inverted into a light lavender box there.
const BUBBLE_BG = `var(--kula-dark-secondary-200, ${colors['secondary-950'] as string})`;
const BUBBLE_TEXT = 'var(--kula-dark-secondary-950, #FFFFFF)';

export interface KTooltipWithActionItem {
  label: string;
  onClick: () => void;
  icon?: React.ElementType;
  isLoading?: boolean;
  disabled?: boolean;
  tooltip?: string;
}

export interface KTooltipWithActionProps {
  trigger: React.ReactElement;
  message: string;
  action?: KTooltipWithActionItem;
  placement?: PopoverProps['placement'];
  width?: string;
}

export const KTooltipWithAction: React.FC<KTooltipWithActionProps> = ({
  trigger,
  message,
  action,
  placement = 'bottom',
  width = '260px',
}) => {
  return (
    <Popover trigger="click" placement={placement} isLazy>
      <PopoverTrigger>{trigger}</PopoverTrigger>
      <Portal>
        <PopoverContent
          bg={BUBBLE_BG}
          border="none"
          borderRadius="10px"
          boxShadow="0px 8px 24px rgba(0, 0, 0, 0.2)"
          w={width}
          maxW={width}
          _focus={{ outline: 'none' }}
        >
          <PopoverArrow bg={BUBBLE_BG} />
          <PopoverBody p="12px">
            <Flex flexDir="column" gap="10px">
              <KText
                fontSize="14px"
                fontWeight="500"
                color={BUBBLE_TEXT}
                lineHeight="150%"
              >
                {message}
              </KText>

              {action && (
                <Tooltip
                  label={action.disabled ? action.tooltip : undefined}
                  isDisabled={!action.disabled || !action.tooltip}
                  placement="auto"
                  hasArrow
                  shouldWrapChildren
                >
                  <Flex
                    as="button"
                    alignItems="center"
                    justifyContent="center"
                    gap="6px"
                    bg="base-white"
                    borderRadius="8px"
                    py="8px"
                    px="12px"
                    w="100%"
                    cursor={
                      action.disabled || action.isLoading
                        ? 'not-allowed'
                        : 'pointer'
                    }
                    _hover={
                      action.disabled || action.isLoading
                        ? { bg: 'secondary-25' }
                        : { bg: 'secondary-25' }
                    }
                    onClick={
                      action.disabled || action.isLoading
                        ? undefined
                        : action.onClick
                    }
                  >
                    {action.isLoading ? (
                      <Spinner size="xs" color="secondary-700" />
                    ) : action.icon ? (
                      <Icon
                        as={action.icon}
                        boxSize="14px"
                        color="secondary-700"
                      />
                    ) : null}
                    <KText
                      fontSize="13px"
                      fontWeight="500"
                      color="secondary-700"
                    >
                      {action.label}
                    </KText>
                  </Flex>
                </Tooltip>
              )}
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
